<script setup lang="ts">
import { StarterKit } from '@tiptap/starter-kit'
import { ImageResize } from 'tiptap-extension-resize-image'
import { FontSize } from 'tiptap-extension-font-size'
import { TextStyle } from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import { Link } from '@tiptap/extension-link'
import { CharacterCount } from '@tiptap/extension-character-count'
import { TextAlign } from '@tiptap/extension-text-align'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { Youtube } from '@tiptap/extension-youtube'
import { FileHandler } from '@tiptap-pro/extension-file-handler'
import { safeGenerateHTML } from '@/utils/safeGenerateHTML'
import TipTapVideoExtension from '@/extensions/TipTapVideoExtension'
import type { SortItem } from '@core/types'
import type { TableOptions } from '@core/stores/user'
import { App } from '@/types/generated'
import UserData = App.Http.Controllers.Auth.UserData

const { t } = useI18n()
const table = 'scenarios'

const userData = useCookie<UserData>('userData')
const userRoles: string[] = userData.value?.roles ?? []

definePage({
  meta: {
    action: 'read',
    subject: 'scenarios',
  },
})

const headers = [

  { title: '', key: 'data-table-expand' },
  { title: t(`${table}-page.title`), key: 'title' },
  { title: t(`${table}-page.competence`), key: 'competence' },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const defaultTableOptions: TableOptions = {
  sortBy: 'id',
  orderBy: 'asc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const useLoading = false
const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const itemForDelete = ref()
const currenItem = ref()
const isLoading = ref(false)
const isGenerateDialogOpen = ref(false)
const isAddDialogOpen = ref(false)
const isCopyDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)
const selectedCompetence = ref()

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const { data: itemsData, execute: fetchItems } = await useApi<any>(createUrl(`/${table}/`,
  {
    query: {
      search: debouncedSearch,
      competence_id: selectedCompetence,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
    },
  },
))

const { data: competenciesList } = await useApi<any>('/competencies/all')

let competencies = [{ title: '', id: '1' }]
if (competenciesList?.value)
  competencies = competenciesList.value

const updateList = async () => {
  isLoading.value = useLoading
  fetchItems().then(() => {
    isLoading.value = false
  })
}

const deleteItem = async () => {
  await $api(`/${table}/`, {
    method: 'DELETE',
    body: {
      id: itemForDelete.value.id,
    },
  })
  await updateList()
}

const deleteConfirmation = async (item: any) => {
  isDeleteConfirmDialogVisible.value = !isDeleteConfirmDialogVisible.value
  itemForDelete.value = item
}

const showCopyDialog = async (item: any) => {
  isCopyDialogOpen.value = !isCopyDialogOpen.value
  currenItem.value = item
}

const items = computed(() => itemsData.value?.data)

const total = computed(() => itemsData.value?.total)

const editorExtensions = [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3],
    },
  }),
  TipTapVideoExtension.configure({ HTMLAttributes: { class: 'tiptap-video', controls: true } }),
  ImageResize.configure({
    allowBase64: true,
    inline: true,
  }),
  FontSize,
  TextStyle,
  Underline,
  Link.configure(
    {
      openOnClick: false,
    },
  ),
  CharacterCount.configure({
    limit: 240,
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,
  Youtube.configure({
    inline: false,
    nocookie: true,
  }),
  FileHandler.configure({
    allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    onDrop: (currentEditor, files, pos) => {
      files.forEach(file => {
        const fileReader = new FileReader()

        fileReader.readAsDataURL(file)
        fileReader.onload = () => {
          currentEditor.chain().insertContentAt(pos, {
            type: 'image',
            attrs: {
              src: fileReader.result,
            },
          }).focus().run()
        }
      })
    },
    onPaste: (currentEditor, files) => {
      files.forEach(file => {
        const fileReader = new FileReader()

        fileReader.readAsDataURL(file)
        fileReader.onload = () => {
          currentEditor.chain().insertContentAt(currentEditor.state.selection.anchor, {
            type: 'image',
            attrs: {
              src: fileReader.result,
            },
          }).focus().run()
        }
      })
    },
  }),
]

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([page, itemsPerPage, sortBy, orderBy], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
  })
})
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />

          <AppSelect
            v-model="selectedCompetence"
            clearable
            clear-icon="tabler-x"
            :items="competencies"
            item-title="title"
            item-value="id"
            style=" min-inline-size: 200px;"
            :placeholder="t(`${table}-page.competence`)"
          />
        </div>
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            prepend-icon="tabler-plus"
            @click="$router.push('/games/scenarios/add')"
          >
            {{ t(`${table}-page.add`) }}
          </VBtn>
          <VBtn
            prepend-icon="tabler-sparkles"
            color="success"
            class="violette-gradient-button"
            @click="isGenerateDialogOpen = !isGenerateDialogOpen"
          >
            {{ t(`${table}-page.ai-generate`) }}
          </VBtn>
        </div>
      </VCardText>
      <div class="category-table">
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items="items"
          :items-length="total"
          class="text-no-wrap"
          expand-on-click
          show-expand
          @update:options="updateOptions"
        >
          <!-- Expanded Row Data -->
          <template #expanded-row="slotProps">
            <tr class="v-data-table__tr">
              <td :colspan="headers.length">
                <div
                  v-dompurify-html="safeGenerateHTML(
                    slotProps.item.description, editorExtensions)"
                  class="description-content my-4 max-w-full"
                  style="max-width: 100%"
                />
              </td>
            </tr>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex align-items-end justify-end">
              <IconBtn
                v-if="userIsAdmin(userRoles) || userIsScopedAdmin(userRoles)"
                tag="a"
                :href="`/api/scenarios/export-docx/${item.id}`"
                target="_blank"
                rel="noopener noreferrer"
                :title="t(`${table}-page.export-docx`)"
              >
                <VIcon icon="tabler-file-type-doc" />
              </IconBtn>
              <IconBtn>
                <VIcon
                  icon="tabler-edit"
                  :title="t(`${table}-page.edit`)"
                  @click="$router.push(`/games/scenarios/${item.id}`)"
                />
              </IconBtn>
              <IconBtn>
                <VIcon
                  icon="tabler-copy"
                  :title="t(`${table}-page.copy`)"
                  @click="showCopyDialog(item)"
                />
              </IconBtn>
              <IconBtn>
                <VIcon
                  :title="t(`${table}-page.delete`)"
                  icon="tabler-trash"
                  @click="deleteConfirmation(item)"
                />
              </IconBtn>
            </div>
          </template>
          <template #item.title="{ item }">
            <div
              v-if="item.has_img === true"
              class="d-flex align-center gap-x-4"
            >
              <VAvatar
                size="38"
                variant="tonal"
                rounded
                :image="`/api/scenarios/image/${item.id}`"
              />
              <span>{{ item.title }}</span>
              <VBadge
                v-if="item.ai_profile_id"
                content="ai"
                inline
                color="success"
              />
            </div>
            <span v-else>{{ item.title }}</span>
            <VBadge
              v-if="item.ai_profile_id && item.has_img !== true"
              content="ai"
              inline
              class="ml-4"
              color="success"
            />
          </template>
          <template #item.competence="{ item }">
            <VChip color="primary">
              {{ item.competence?.title }}
            </VChip>
          </template>
          <template #bottom>
            <div>
              <VDivider />

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.ceil(total / itemsPerPage)"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>
    <ScenarioCopyDialog
      v-model:isDialogVisible="isCopyDialogOpen"
      :scenario-id="currenItem?.id"
      @submit="updateList"
    />
    <ScenarioAIGenerateDialog
      v-model:isDialogVisible="isGenerateDialogOpen"
      @submit="updateList"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmDialogVisible"
      :confirmation-question="t('confirmation.sure')"
      cancel-msg=""
      :cancel-title="t('confirmation.cancel')"
      :confirm-msg="t('confirmation.deleted')"
      :confirm-title="t('confirmation.success')"
      @confirm="deleteItem"
    />
  </div>
</template>

<style lang="scss">
.ProseMirror-focused{
  border: none;
}
</style>
